import { useDateTimeDisplay } from '../../util/useDateTimeDisplay';
import CalendarDateField from '../../util/CalendarDateField';
import CalendarTimeField from '../../util/CalendarTimeField';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddTaskIcon from "@mui/icons-material/AddTask";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import TelegramIcon from "@mui/icons-material/Telegram";
import GoogleIcon from "@mui/icons-material/Google";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { UserContext } from "../../components/auth/auth-context";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../graphql/queries";
import { useSelectedBusiness } from "../../redux/store";
import { getToken } from "../../util/authUtils";
import { getApiBaseUrl } from "../../util/apiBaseUrl";
import { getEffectivePlan } from "../../util/utils";

const CAPTURE_SCAN_LIMITS = {
  FREE: 5,
  STANDARD: 50,
  PREMIUM: 250,
  ENTERPRISE: null,
};
const BOOKING_PRICING_PATH = "/biz/pricing?product=booking&v=booking";
const LEGACY_CUSTOM_LINKS_TITLE = "Links from this Timely365 page";

function getPlanTierCode(rawCode) {
  const code = String(rawCode || "").trim().toUpperCase();
  if (code.startsWith("BOOKING_")) return code.replace("BOOKING_", "");
  if (code.startsWith("CARE_")) return code.replace("CARE_", "");
  if (code.includes("ENTERPRISE")) return "ENTERPRISE";
  if (code.includes("PREMIUM")) return "PREMIUM";
  if (code.includes("STANDARD")) return "STANDARD";
  return "FREE";
}

function getCurrentUsagePeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getCaptureUsageKey(businessId, period = getCurrentUsagePeriod()) {
  return `timely365:capture-scan-usage:${businessId || "unknown"}:${period}`;
}

function readCaptureScanUsage(businessId) {
  if (typeof window === "undefined" || !businessId) return 0;
  const count = Number(window.localStorage.getItem(getCaptureUsageKey(businessId)) || 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function incrementCaptureScanUsage(businessId) {
  if (typeof window === "undefined" || !businessId) return 0;
  const next = readCaptureScanUsage(businessId) + 1;
  window.localStorage.setItem(getCaptureUsageKey(businessId), String(next));
  return next;
}


function splitOcrText(value) {
  const raw = String(value || "");
  const parts = raw.split(/\bOCR text\s*:/i);
  return {
    visible: parts[0]?.trim() || "",
    raw: parts.length > 1 ? parts.slice(1).join("OCR text:").trim() : "",
  };
}

function normalizeOcrComparison(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function getDisplayNotes(extracted) {
  const split = splitOcrText(extracted?.notes);
  const raw = String(extracted?.rawOcrText || split.raw || "").trim();
  if (raw && normalizeOcrComparison(split.visible) === normalizeOcrComparison(raw)) return "";
  return split.visible;
}

function getEditableCustomLinksTitle(value) {
  const title = String(value || "").trim();
  return title === LEGACY_CUSTOM_LINKS_TITLE ? "" : title;
}

function getRawImageText(extracted) {
  return String(extracted?.rawOcrText || splitOcrText(extracted?.notes).raw || "").trim();
}

async function readJsonResponse(response, fallbackMessage) {
  const contentType = response.headers?.get?.("content-type") || "";
  const text = await response.text();
  if (/application\/json/i.test(contentType) && text) {
    try {
      return JSON.parse(text);
    } catch (_error) {
      throw new Error(response.ok ? fallbackMessage : "Capture service returned an incomplete response. Refresh to see whether it was saved.");
    }
  }
  if (text) {
    throw new Error(response.ok ? fallbackMessage : text.slice(0, 220));
  }
  if (!response.ok) {
    throw new Error(fallbackMessage);
  }
  return {};
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read this image."));
    reader.readAsDataURL(file);
  });
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load this image."));
    image.src = src;
  });
}

async function prepareCaptureImage(file) {
  const originalDataUrl = await readFileAsDataUrl(file);
  const image = await loadImageElement(originalDataUrl);
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
  const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
  const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return originalDataUrl;
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.82);
}


function formatDisplayTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const amPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)$/i);
  if (amPm) {
    const hour = Number(amPm[1]);
    const minute = Number(amPm[2] || 0);
    const meridiem = amPm[3].replace(/\./g, "").toUpperCase();
    return `${hour}:${String(minute).padStart(2, "0")} ${meridiem}`;
  }
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))$/);
  if (!match) return raw;
  const hour24 = Number(match[1]);
  const minute = Number(match[2] || 0);
  if (!Number.isFinite(hour24) || hour24 < 0 || hour24 > 23) return raw;
  const meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${meridiem}`;
}

function displayCaptureStatus(item) {
  const type = item?.extracted?.captureType;
  const status = item?.status || "PENDING";
  if ((type === "place" || type === "contact") && item?.status !== "NEEDS_ACCOUNT" && item?.status !== "IGNORED") {
    return "SAVED";
  }
  if (type === "poster" && item?.status !== "NEEDS_ACCOUNT" && item?.status !== "IGNORED") {
    return item?.extracted?.shareEnabled ? "Ad link" : "Ad poster";
  }
  if (status !== "PENDING") return status;
  const extracted = item?.extracted || {};
  if (extracted.date && extracted.startTime && !isGenericEventTitle(extracted.title)) return "Ready";
  if (extracted.date && extracted.startTime) return "Review";
  return "Needs details";
}

function statusColor(status) {
  if (status === "ADDED" || status === "SAVED" || status === "Ready") return "success";
  if (status === "Ad poster" || status === "Ad link") return "info";
  if (status === "IGNORED") return "default";
  if (status === "NEEDS_ACCOUNT" || status === "Review" || status === "Needs details") return "warning";
  return "info";
}

function isGenericSavedTitle(value) {
  const title = String(value || "").trim();
  return !title || /^saved\s+(place|address|contact)\b/i.test(title) || /^untitled$/i.test(title);
}

function isGenericEventTitle(value) {
  const title = String(value || "").trim();
  return !title || /^(event|untitled event|captured event|event from poster|event at .+)$/i.test(title);
}

function getCaptureTitle(item) {
  const extracted = item?.extracted || {};
  const type = extracted.captureType;
  const internalName = String(extracted.internalName || "").trim();
  if (internalName) return internalName;
  const address = getCaptureAddress(extracted);
  if (type === "place" && isGenericSavedTitle(extracted.title)) return address || "Untitled";
  if (type === "contact" && isGenericSavedTitle(extracted.title)) {
    return extracted.businessName || extracted.contactName || getCapturePhones(extracted)[0] || address || "Untitled";
  }
  return extracted.title || extracted.businessName || extracted.contactName || "Untitled";
}

function normalizePhoneList(...values) {
  const phones = [];
  const append = (value) => {
    if (Array.isArray(value)) {
      value.forEach(append);
      return;
    }
    if (value && typeof value === "object") {
      append(value.phone || value.phoneNumber || value.value || value.number);
      return;
    }
    String(value || "")
      .split(/[;,]/)
      .map((phone) => phone.trim())
      .filter(Boolean)
      .forEach((phone) => phones.push(phone));
  };
  values.forEach(append);
  return [...new Set(phones)];
}

function getCapturePhones(extracted) {
  return normalizePhoneList(extracted?.phones, extracted?.phoneNumbers, extracted?.phone);
}

function parseTimeValue(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  const amPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (amPm) {
    let hour = Number(amPm[1]);
    const minute = Number(amPm[2] || 0);
    if (amPm[3] === "pm" && hour < 12) hour += 12;
    if (amPm[3] === "am" && hour === 12) hour = 0;
    return { hour, minute };
  }
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?$/);
  return match ? { hour: Number(match[1]), minute: Number(match[2] || 0) } : null;
}

function getEditTimeParts(value) {
  const display = formatDisplayTime(value);
  const match = display.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return { time: display || "", meridiem: "PM" };
  return {
    time: `${Number(match[1])}:${String(Number(match[2] || 0)).padStart(2, "0")}`,
    meridiem: match[3].toUpperCase(),
  };
}


function buildEditStartTime(values) {
  const rawTime = String(values?.time || values?.startTime || "").trim();
  if (!rawTime) return "";
  const meridiem = String(values?.meridiem || "PM").toUpperCase() === "AM" ? "AM" : "PM";
  const match = rawTime.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!match) return rawTime;
  const hour = Math.min(12, Math.max(1, Number(match[1] || 1)));
  const minute = Math.min(59, Math.max(0, Number(match[2] || 0)));
  return `${hour}:${String(minute).padStart(2, "0")} ${meridiem}`;
}

function toGoogleStamp(date, time, addHours = 0) {
  const parsedTime = parseTimeValue(time);
  if (!date || !parsedTime) return null;
  const local = new Date(`${date}T${String(parsedTime.hour).padStart(2, "0")}:${String(parsedTime.minute).padStart(2, "0")}:00`);
  if (Number.isNaN(local.getTime())) return null;
  local.setHours(local.getHours() + addHours);
  return local.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function buildGoogleCalendarUrl(extracted) {
  const start = toGoogleStamp(extracted?.date, extracted?.startTime);
  const end = extracted?.endTime
    ? toGoogleStamp(extracted?.date, extracted?.endTime)
    : toGoogleStamp(extracted?.date, extracted?.startTime, 1);
  if (!start || !end) return null;
  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", extracted?.title || "Captured event");
  url.searchParams.set("dates", `${start}/${end}`);
  if (extracted?.location) url.searchParams.set("location", extracted.location);
  const notes = getDisplayNotes(extracted);
  if (notes) url.searchParams.set("details", notes);
  return url.toString();
}

function getCaptureAddress(extracted) {
  return String(extracted?.address || extracted?.location || "").trim();
}

function buildGoogleDirectionsUrl(extracted) {
  const address = getCaptureAddress(extracted);
  if (!address) return null;
  const url = new URL("https://www.google.com/maps/dir/");
  url.searchParams.set("api", "1");
  url.searchParams.set("destination", address);
  return url.toString();
}

function normalizeExternalUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function normalizeCustomLinks(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const url = normalizeExternalUrl(item?.url);
      if (!url) return null;
      let fallbackLabel = "Open link";
      try {
        fallbackLabel = new URL(url).hostname.replace(/^www\./i, "") || fallbackLabel;
      } catch (_error) {
        fallbackLabel = "Open link";
      }
      return {
        label: String(item?.label || "").trim() || fallbackLabel,
        url,
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

function getCustomLinks(extracted) {
  return normalizeCustomLinks(extracted?.customLinks);
}

function isPosterCapture(itemOrExtracted) {
  const extracted = itemOrExtracted?.extracted || itemOrExtracted || {};
  return extracted.captureType === "poster";
}

function normalizePublicVisibility(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    showDateTime: source.showDateTime !== false,
    showLocation: source.showLocation !== false,
  };
}

function normalizeCaptureDetailText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//i, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getInviteShareUrl(itemOrExtracted) {
  const extracted = itemOrExtracted?.extracted || itemOrExtracted || {};
  const token = String(extracted?.shareToken || "").trim();
  if (!token) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = `/i/${encodeURIComponent(token)}`;
  return origin ? `${origin}${path}` : path;
}

function isInvitePublic(extracted) {
  return extracted?.shareEnabled === true;
}

const actionIconSx = {
  width: 36,
  height: 36,
  border: "1px solid #c8d3df",
  color: "text.primary",
  bgcolor: "background.paper",
  borderRadius: 1,
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "#9fb0c0",
  },
};

const dangerIconSx = {
  ...actionIconSx,
  color: "error.main",
  borderColor: "#f1b8b4",
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "#e58b84",
  },
};

const REMINDER_OPTIONS = [
  { value: 0, label: "No reminder" },
  { value: 15, label: "15 min before" },
  { value: 30, label: "30 min before" },
  { value: 60, label: "1 hour before" },
  { value: 120, label: "2 hours before" },
  { value: 1440, label: "1 day before" },
  { value: 2880, label: "2 days before" },
  { value: 10080, label: "1 week before" },
];

export default function TimelyCaptureInbox() {
  const { displayCalendar, displayClock, dateLabel, clockLabel } = useDateTimeDisplay();
  const [editDateValid, setEditDateValid] = useState(true);
  const [quickDateValid, setQuickDateValid] = useState(true);
  const formatCaptureTime = extracted => [dateLabel(extracted?.date), clockLabel(formatDisplayTime(extracted?.startTime))].filter(Boolean).join(' at ');
  const clockValue = values => { const p = parseTimeValue(buildEditStartTime(values)); return p ? String(p.hour).padStart(2, '0') + ':' + String(p.minute).padStart(2, '0') : ''; };
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const businessId = Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId || 0) || null;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [scanUsageCount, setScanUsageCount] = useState(() => readCaptureScanUsage(businessId));
  const [serverScanUsage, setServerScanUsage] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingPlaceId, setSavingPlaceId] = useState(null);
  const [reminderSavingId, setReminderSavingId] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [activeTab, setActiveTab] = useState("events");
  const [reminderValues, setReminderValues] = useState({});
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuItemId, setActionMenuItemId] = useState(null);
  const [quickDialog, setQuickDialog] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickValues, setQuickValues] = useState({});
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanText, setScanText] = useState("");
  const [scanImageDataUrl, setScanImageDataUrl] = useState("");
  const [scanImageName, setScanImageName] = useState("");
  const [scanReminderMinutes, setScanReminderMinutes] = useState(1440);
  const [scanSaving, setScanSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const compactActions = useMediaQuery("(max-width:700px)");

  const { data: bookingSubscriptionData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId, productKey: "booking" },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(GET_PLANS, {
    variables: { product: "booking" },
  });

  const capturePlanTier = useMemo(() => {
    const effectivePlan = Number(getEffectivePlan(providerUser) || 0);
    if (effectivePlan >= 4) return "ENTERPRISE";
    if (effectivePlan >= 3) return "PREMIUM";
    if (effectivePlan >= 2) return "STANDARD";

    const subscription = bookingSubscriptionData?.getBillingSubscription;
    const status = String(subscription?.status || "").toLowerCase();
    const hasActiveSubscription = ["trialing", "active", "past_due", "canceling"].includes(status);
    if (!hasActiveSubscription) return "FREE";

    const currentPlan = (plansData?.plans || []).find(
      (plan) => String(plan?.id) === String(subscription?.planId)
    );
    return getPlanTierCode(currentPlan?.code);
  }, [bookingSubscriptionData?.getBillingSubscription, plansData?.plans, providerUser]);

  const captureScanLimit =
    serverScanUsage && serverScanUsage.limit !== undefined
      ? serverScanUsage.limit
      : CAPTURE_SCAN_LIMITS[capturePlanTier] ?? null;
  const effectiveScanUsageCount =
    serverScanUsage && Number.isFinite(Number(serverScanUsage.used))
      ? Number(serverScanUsage.used)
      : scanUsageCount;
  const captureScansRemaining =
    captureScanLimit == null ? null : Math.max(0, captureScanLimit - effectiveScanUsageCount);
  const captureScanLimitReached = captureScanLimit != null && effectiveScanUsageCount >= captureScanLimit;
  const isCaptureLimitError = (value) =>
    /capture scan limit/i.test(String(value || ""));
  const captureScanUsageLabel =
    captureScanLimit == null
      ? `${effectiveScanUsageCount} scans used this month`
      : `${effectiveScanUsageCount}/${captureScanLimit} scans used this month`;

  useEffect(() => {
    setScanUsageCount(readCaptureScanUsage(businessId));
    setServerScanUsage(null);
  }, [businessId]);

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        const isPlace = item?.extracted?.captureType === "place";
        const isContact = item?.extracted?.captureType === "contact";
        const isPoster = isPosterCapture(item);
        if (activeTab === "addresses") return isPlace;
        if (activeTab === "contacts") return isContact;
        if (activeTab === "posters") return isPoster;
        return !isPlace && !isContact && !isPoster;
      }),
    [activeTab, items]
  );

  const eventCount = useMemo(
    () => items.filter((item) => !["place", "contact", "poster"].includes(item?.extracted?.captureType)).length,
    [items]
  );
  const addressCount = items.filter((item) => item?.extracted?.captureType === "place").length;
  const contactCount = items.filter((item) => item?.extracted?.captureType === "contact").length;
  const posterCount = items.filter((item) => isPosterCapture(item)).length;

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuItemId(null);
  };

  const openQuickSchedule = () => {
    setQuickDateValid(true);
    setQuickDialog("event");
    setQuickValues({
      title: "",
      date: "",
      time: "",
      meridiem: "PM",
      location: "",
      notes: "",
      reminderMinutes: 1440,
    });
  };

  const openQuickAddress = () => {
    setQuickDateValid(true);
    setQuickDialog("place");
    setQuickValues({
      title: "",
      businessName: "",
      contactName: "",
      address: "",
      phone: "",
      phones: [""],
      website: "",
      notes: "",
    });
  };

  const openQuickContact = () => {
    setQuickDateValid(true);
    setQuickDialog("contact");
    setQuickValues({
      title: "",
      businessName: "",
      contactName: "",
      address: "",
      phone: "",
      phones: [""],
      website: "",
      notes: "",
    });
  };

  const closeQuickDialog = () => {
    if (quickSaving) return;
    setQuickDialog(null);
    setQuickValues({});
  };

  const updateQuickValue = (key, value) => {
    setQuickValues((current) => ({ ...current, [key]: value }));
  };

  const handleQuickPhoneChange = (index, value) => {
    setQuickValues((current) => {
      const phones = [...(Array.isArray(current.phones) && current.phones.length ? current.phones : [""])];
      phones[index] = value;
      return { ...current, phones, phone: normalizePhoneList(phones)[0] || "" };
    });
  };

  const addQuickPhone = () => {
    setQuickValues((current) => ({
      ...current,
      phones: [...(Array.isArray(current.phones) && current.phones.length ? current.phones : [""]), ""],
    }));
  };

  const removeQuickPhone = (index) => {
    setQuickValues((current) => {
      const phones = (Array.isArray(current.phones) && current.phones.length ? current.phones : [""]).filter(
        (_, phoneIndex) => phoneIndex !== index
      );
      const nextPhones = phones.length ? phones : [""];
      return { ...current, phones: nextPhones, phone: normalizePhoneList(nextPhones)[0] || "" };
    });
  };

  const loadDrafts = useCallback(async (options = {}) => {
    const showLoading = options.showLoading !== false;
    if (showLoading) setLoading(true);
    setError("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts`, {
        headers: { authorization: token ? `Bearer ${token}` : "" },
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not load Capture drafts.");
      }
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setServerScanUsage(payload.captureScanUsage || null);
    } catch (err) {
      setError(err.message || "Could not load Capture drafts.");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [apiBaseUrl]);

  const openScanDialog = async () => {
    await loadDrafts({ showLoading: false });
    setScanDialogOpen(true);
  };

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  useEffect(() => {
    setReminderValues((current) => {
      const next = { ...current };
      let changed = false;
      items.forEach((item) => {
        if (item?.reminderMinutes == null || next[item.id] != null) return;
        next[item.id] = Number(item.reminderMinutes);
        changed = true;
      });
      return changed ? next : current;
    });
  }, [items]);

  const addToCalendar = async (draftId) => {
    setSavingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}/add`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          reminderMinutes: Number(reminderValues[draftId] ?? 1440),
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not add this capture to calendar.");
      }
      setItems((current) =>
        current.map((item) => (Number(item.id) === Number(draftId) ? payload.draft : item))
      );
      setNotice("Capture added to your Timely365 calendar.");
    } catch (err) {
      setError(err.message || "Could not add this capture to calendar.");
    } finally {
      setSavingId(null);
    }
  };

  const updateCalendarReminder = async (draftId) => {
    setReminderSavingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}/reminder`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          reminderMinutes: Number(reminderValues[draftId] ?? 1440),
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not update this reminder.");
      }
      setItems((current) =>
        current.map((item) => (Number(item.id) === Number(draftId) ? payload.draft : item))
      );
      setNotice("Reminder updated.");
    } catch (err) {
      setError(err.message || "Could not update this reminder.");
    } finally {
      setReminderSavingId(null);
    }
  };

  const startEdit = (item) => {
    setEditDateValid(true);
    const extracted = item.extracted || {};
    const timeParts = getEditTimeParts(extracted.startTime);
    const address = getCaptureAddress(extracted);
    const phones = getCapturePhones(extracted);
    setEditingId(item.id);
    setEditValues({
      captureType: extracted.captureType || "event",
      internalName: extracted.internalName || "",
      title: extracted.title || "",
      businessName: extracted.businessName || "",
      contactName: extracted.contactName || "",
      date: extracted.date || "",
      time: timeParts.time,
      meridiem: timeParts.meridiem,
      location: extracted.location || "",
      address,
      phone: phones[0] || "",
      phones: phones.length ? phones : [""],
      website: extracted.website || "",
      customLinksTitle: getEditableCustomLinksTitle(extracted.customLinksTitle),
      customLinks: getCustomLinks(extracted),
      publicVisibility: normalizePublicVisibility(extracted.publicVisibility),
      notes: getDisplayNotes(extracted),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleEditPhoneChange = (index, value) => {
    setEditValues((current) => {
      const phones = [...(Array.isArray(current.phones) && current.phones.length ? current.phones : [""])];
      phones[index] = value;
      return { ...current, phones, phone: normalizePhoneList(phones)[0] || "" };
    });
  };

  const addEditPhone = () => {
    setEditValues((current) => ({
      ...current,
      phones: [...(Array.isArray(current.phones) && current.phones.length ? current.phones : [""]), ""],
    }));
  };

  const removeEditPhone = (index) => {
    setEditValues((current) => {
      const phones = (Array.isArray(current.phones) && current.phones.length ? current.phones : [""]).filter(
        (_, phoneIndex) => phoneIndex !== index
      );
      const nextPhones = phones.length ? phones : [""];
      return { ...current, phones: nextPhones, phone: normalizePhoneList(nextPhones)[0] || "" };
    });
  };

  const saveDraft = async (draftId) => {
    if (!editDateValid) return;
    setSavingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          extracted: {
            ...editValues,
            phones: normalizePhoneList(editValues.phones),
            phone: normalizePhoneList(editValues.phones)[0] || "",
            customLinksTitle: getEditableCustomLinksTitle(editValues.customLinksTitle),
            customLinks: normalizeCustomLinks(editValues.customLinks),
            publicVisibility:
              editValues.captureType === "poster"
                ? {
                    ...normalizePublicVisibility(editValues.publicVisibility),
                    showDateTime: false,
                    showLocation: false,
                  }
                : normalizePublicVisibility(editValues.publicVisibility),
            date: editValues.captureType === "poster" ? "" : editValues.date,
            startTime: editValues.captureType === "event" ? buildEditStartTime(editValues) : "",
            endTime: editValues.captureType === "poster" ? "" : editValues.endTime,
            location: editValues.captureType === "poster" ? "" : editValues.location,
            address:
              editValues.captureType === "poster"
                ? ""
                : editValues.captureType === "event"
                  ? editValues.location
                  : editValues.address,
          },
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not update this capture.");
      }
      setItems((current) =>
        current.map((item) => (Number(item.id) === Number(draftId) ? payload.draft : item))
      );
      cancelEdit();
      setNotice("Capture updated.");
    } catch (err) {
      setError(err.message || "Could not update this capture.");
    } finally {
      setSavingId(null);
    }
  };

  const moveCaptureToType = async (item, captureType) => {
    const draftId = item?.id;
    if (!draftId) return;
    setSavingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const extracted = item.extracted || {};
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          extracted: {
            ...extracted,
            captureType,
            ...(captureType === "poster"
              ? {
                  date: "",
                  startTime: "",
                  endTime: "",
                  location: "",
                  address: "",
                  publicVisibility: {
                    ...normalizePublicVisibility(extracted.publicVisibility),
                    showDateTime: false,
                    showLocation: false,
                  },
                }
              : {
                  publicVisibility: normalizePublicVisibility(extracted.publicVisibility),
                }),
          },
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not move this capture.");
      }
      setItems((current) =>
        current.map((currentItem) => (Number(currentItem.id) === Number(draftId) ? payload.draft : currentItem))
      );
      setActiveTab(
        captureType === "place"
          ? "addresses"
          : captureType === "contact"
            ? "contacts"
            : captureType === "poster"
              ? "posters"
              : "events"
      );
      setNotice(`Capture moved to ${captureType === "place" ? "addresses" : captureType === "poster" ? "posters" : `${captureType}s`}.`);
    } catch (err) {
      setError(err.message || "Could not move this capture.");
    } finally {
      setSavingId(null);
    }
  };

  const removeDraft = async (draftOrId) => {
    const draftId = typeof draftOrId === "object" ? draftOrId.id : draftOrId;
    const hasCalendarItem = Boolean(typeof draftOrId === "object" && draftOrId.calendarBookingId);
    if (hasCalendarItem) {
      const confirmed = window.confirm(
        "Remove this Capture and its Timely365 calendar item? If you added it to Google, Apple, Outlook, or another calendar, delete it there separately."
      );
      if (!confirmed) return;
    }
    setRemovingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}`, {
        method: "DELETE",
        headers: { authorization: token ? `Bearer ${token}` : "" },
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not remove this capture.");
      }
      setItems((current) => current.filter((item) => Number(item.id) !== Number(draftId)));
      setNotice(
        payload.calendarRemoved
          ? "Capture and Timely365 calendar item removed. Delete it separately from any external calendar."
          : "Capture removed."
      );
      if (Number(editingId) === Number(draftId)) cancelEdit();
    } catch (err) {
      setError(err.message || "Could not remove this capture.");
    } finally {
      setRemovingId(null);
    }
  };

  const saveAddress = async (draftId) => {
    setSavingPlaceId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}/save-place`, {
        method: "POST",
        headers: { authorization: token ? `Bearer ${token}` : "" },
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not save this address.");
      }
      setItems((current) => [payload.draft, ...current]);
      setNotice("Address saved to Capture.");
    } catch (err) {
      setError(err.message || "Could not save this address.");
    } finally {
      setSavingPlaceId(null);
    }
  };

  const createQuickCapture = async () => {
    setQuickSaving(true);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const isPlace = quickDialog === "place";
      const isContact = quickDialog === "contact";
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          addToCalendar: !isPlace && !isContact,
          reminderMinutes: Number(quickValues.reminderMinutes ?? 1440),
          extracted: isPlace || isContact
            ? {
              captureType: isContact ? "contact" : "place",
              title: quickValues.title || quickValues.businessName || quickValues.contactName || (isContact ? "Saved contact" : "Saved address"),
              businessName: quickValues.businessName,
              contactName: quickValues.contactName,
              address: quickValues.address,
              phone: normalizePhoneList(quickValues.phones)[0] || "",
              phones: normalizePhoneList(quickValues.phones),
              website: quickValues.website,
              notes: quickValues.notes,
            }
            : {
              captureType: "event",
              title: quickValues.title,
              date: quickValues.date,
              startTime: buildEditStartTime(quickValues),
              location: quickValues.location,
              notes: quickValues.notes,
            },
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not create this capture.");
      }
      setItems((current) => [payload.draft, ...current]);
      setActiveTab(isContact ? "contacts" : isPlace ? "addresses" : "events");
      setNotice(isContact ? "Contact added to Capture." : isPlace ? "Address added to your address book." : "Quick schedule added to your Timely365 calendar.");
      setQuickDialog(null);
      setQuickValues({});
    } catch (err) {
      setError(err.message || "Could not create this capture.");
    } finally {
      setQuickSaving(false);
    }
  };

  const setScanImageFile = async (file) => {
    if (!file) return;
    if (!/^image\//i.test(file.type || "")) {
      setError("Please choose an image file.");
      return;
    }
    try {
      const dataUrl = await prepareCaptureImage(file);
      setScanImageDataUrl(dataUrl);
      setScanImageName(file.name || "Pasted image");
      setNotice("");
      setError("");
    } catch (err) {
      setError(err.message || "Could not read this image.");
    }
  };

  const handleScanPaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []);
    const image = files.find((file) => /^image\//i.test(file.type || ""));
    if (image) {
      event.preventDefault();
      setScanImageFile(image);
    }
  };

  const clearScanInput = () => {
    setScanText("");
    setScanImageDataUrl("");
    setScanImageName("");
    setScanReminderMinutes(1440);
  };

  const createScannedCapture = async () => {
    if (captureScanLimitReached) {
      setError(
        capturePlanTier === "PREMIUM"
          ? "You have reached your Premium Capture scan limit for this month."
          : "You have reached your Capture scan limit for this month. Upgrade your Booking plan for more scans."
      );
      return;
    }

    setScanSaving(true);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/scan`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          sourceText: scanText,
          imageDataUrl: scanImageDataUrl,
          reminderMinutes: scanReminderMinutes,
        }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.detail || payload.error || "Could not scan this capture.");
      }
      setItems((current) => [payload.draft, ...current]);
      const nextUsageCount = incrementCaptureScanUsage(businessId);
      setScanUsageCount(nextUsageCount);
      setServerScanUsage(payload.captureScanUsage || null);
      const type = payload.draft?.extracted?.captureType;
      setActiveTab(
        type === "contact"
          ? "contacts"
          : type === "place"
            ? "addresses"
            : type === "poster"
              ? "posters"
              : "events"
      );
      setNotice(
        payload.booking
          ? "Capture scanned and added to your Timely365 calendar with a reminder."
          : type === "place" || type === "contact"
            ? "Capture scanned and saved."
            : "Capture scanned. Review it before adding to your calendar."
      );
      clearScanInput();
      setScanDialogOpen(false);
      loadDrafts();
    } catch (err) {
      setError(err.message || "Could not scan this capture.");
    } finally {
      setScanSaving(false);
    }
  };

  const createInviteLink = async (draftId, options = {}) => {
    const shouldCopy = options.copy !== false;
    const shouldOpen = Boolean(options.open);
    const publicShare = options.publicShare === true;
    setSharingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}/share`, {
        method: "POST",
        headers: {
          authorization: token ? `Bearer ${token}` : "",
          "content-type": "application/json",
        },
        body: JSON.stringify({ shareEnabled: publicShare }),
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not create invite link.");
      }
      setItems((current) =>
        current.map((item) => (Number(item.id) === Number(draftId) ? payload.draft : item))
      );
      if (payload.shareUrl) {
        if (shouldCopy) {
          await navigator.clipboard?.writeText(payload.shareUrl).catch(() => { });
        }
        setNotice(shouldCopy ? "Invite link copied." : publicShare ? "Public sharing enabled." : "Invite link ready.");
        if (shouldOpen) {
          window.open(payload.shareUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        setNotice("Invite link created.");
      }
    } catch (err) {
      setError(err.message || "Could not create invite link.");
    } finally {
      setSharingId(null);
    }
  };

  const disableInviteLink = async (draftId) => {
    setSharingId(draftId);
    setError("");
    setNotice("");
    try {
      const token = getToken();
      const response = await fetch(`${apiBaseUrl}/api/capture/drafts/${draftId}/share`, {
        method: "DELETE",
        headers: { authorization: token ? `Bearer ${token}` : "" },
      });
      const payload = await readJsonResponse(response, "Capture service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not make this invite private.");
      }
      setItems((current) =>
        current.map((item) => (Number(item.id) === Number(draftId) ? payload.draft : item))
      );
      setNotice("Visitor sharing is off. The owner link still works.");
    } catch (err) {
      setError(err.message || "Could not make this invite private.");
    } finally {
      setSharingId(null);
    }
  };

  const copyInviteLink = async (item) => {
    const existingUrl = getInviteShareUrl(item);
    if (existingUrl) {
      await navigator.clipboard?.writeText(existingUrl).catch(() => { });
      setNotice("Invite link copied.");
      return;
    }
    await createInviteLink(item.id, { copy: true, open: false });
  };

  const openInviteLink = async (item) => {
    const existingUrl = getInviteShareUrl(item);
    if (existingUrl) {
      window.open(existingUrl, "_blank", "noopener,noreferrer");
      return;
    }
    await createInviteLink(item.id, { open: true, copy: false });
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "background.default", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="lg">
        <Stack spacing={2.5}>
          {providerUser?.userId && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                component={RouterLink}
                to={businessId ? "/biz/bookingtypes?v=booking" : "/biz/business?v=booking"}
                variant="outlined"
                startIcon={<ManageAccountsIcon />}
                sx={{ textTransform: "none", borderColor: "#22374a", color: "text.primary" }}
              >
                Manage Business
              </Button>
            </Box>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary" }}>
                Timely365 Capture
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                Review captured events and saved places from your connected sources.
              </Typography>
              <Typography sx={{ color: "#7a8793", fontSize: 13, mt: 0.5 }}>
                Removing an added capture also removes its Timely365 calendar item. Delete it separately from any external calendar.
              </Typography>
              <Typography sx={{ color: captureScanLimitReached ? "#b91c1c" : "#5f6f7d", fontSize: 13, mt: 0.5, fontWeight: 700 }}>
                Capture scans: {captureScanUsageLabel}
                {captureScansRemaining != null ? `, ${captureScansRemaining} remaining` : ", unlimited"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<ImageSearchIcon />}
                onClick={openScanDialog}
                disabled={captureScanLimitReached}
                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              >
                Scan or Paste Capture
              </Button>
              <Button
                component={RouterLink}
                to="/biz/calendar"
                variant="outlined"
                startIcon={<EventAvailableIcon />}
                sx={{ borderColor: "#22374a", color: "text.primary" }}
              >
                Calendar
              </Button>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadDrafts} sx={{ bgcolor: "primary.main" }}>
                Refresh
              </Button>
            </Stack>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {error && isCaptureLimitError(error) && (
            <Button
              component={RouterLink}
              to={BOOKING_PRICING_PATH}
              variant="contained"
              size="small"
              sx={{ alignSelf: "flex-start", bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
            >
              View Booking plans
            </Button>
          )}
          {notice && <Alert severity="success">{notice}</Alert>}

          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid #d8e1ea",
              borderRadius: 1,
              p: { xs: 1.25, sm: 1.5 },
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddTaskIcon />}
                onClick={openQuickSchedule}
                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              >
                Quick Schedule
              </Button>
              <Button
                variant="outlined"
                startIcon={<PersonAddAltIcon />}
                onClick={openQuickContact}
                sx={{ borderColor: "#c8d3df", color: "text.primary" }}
              >
                Add Contact
              </Button>
              <Button
                variant="outlined"
                startIcon={<PlaceIcon />}
                onClick={openQuickAddress}
                sx={{ borderColor: "#c8d3df", color: "text.primary" }}
              >
                Add Address
              </Button>
              <Typography sx={{ color: "#6b7885", fontSize: 13 }}>
                Add something manually when it does not come from a message, poster, or screenshot.
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid #d8e1ea",
              borderRadius: 1,
              px: 1,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 44,
                "& .MuiTab-root": {
                  minHeight: 44,
                  textTransform: "none",
                  fontWeight: 800,
                  color: "text.secondary",
                },
                "& .Mui-selected": { color: "text.primary" },
                "& .MuiTabs-indicator": { bgcolor: "primary.main" },
              }}
            >
              <Tab value="events" label={`Captured Events (${eventCount})`} />
              <Tab value="posters" label={`Ad Posters (${posterCount})`} />
              <Tab value="addresses" label={`Saved Addresses (${addressCount})`} />
              <Tab value="contacts" label={`Contacts (${contactCount})`} />
            </Tabs>
          </Box>

          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography>Loading captures...</Typography>
            </Stack>
          ) : items.length === 0 ? (
            <Alert severity="info" icon={<TelegramIcon />}>
              No captures yet. Forward an event message, poster, business card, screenshot, or address image to @timely365_bot.
            </Alert>
          ) : filteredItems.length === 0 ? (
            <Alert severity="info">
              {activeTab === "addresses"
                ? "No saved addresses yet. Save an address from a captured event or forward an address image to @timely365_bot."
                : activeTab === "contacts"
                  ? "No contacts yet. Forward contact info or add one manually."
                  : activeTab === "posters"
                    ? "No ad posters yet. Scan a promotion, ad, or link-only poster, or move a capture to Ad Posters."
                    : "No captured events yet. Forward an event message, flyer, poster, or screenshot to @timely365_bot."}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {filteredItems.map((item) => {
                const extracted = item.extracted || {};
                const hasCalendarItem = Boolean(item.calendarBookingId);
                const canAdd = !hasCalendarItem && extracted.date && extracted.startTime;
                const googleCalendarUrl = buildGoogleCalendarUrl(extracted);
                const directionsUrl = buildGoogleDirectionsUrl(extracted);
                const isPoster = isPosterCapture(extracted) || activeTab === "posters";
                const isPlace = extracted.captureType === "place" || activeTab === "addresses";
                const isContact = extracted.captureType === "contact" || activeTab === "contacts";
                const isSavedInfo = isPlace || isContact;
                const captureAddress = getCaptureAddress(extracted);
                const capturePhones = getCapturePhones(extracted);
                const capturePhone = capturePhones[0] || "";
                const captureWebsite = normalizeExternalUrl(extracted.website);
                const captureTitleText = getCaptureTitle(item);
                const normalizedTitleText = normalizeCaptureDetailText(captureTitleText);
                const shouldShowAddressLine =
                  Boolean(captureAddress) &&
                  normalizeCaptureDetailText(captureAddress) !== normalizedTitleText;
                const visibleCapturePhones = capturePhones.filter(
                  (phone) => normalizeCaptureDetailText(phone) !== normalizedTitleText
                );
                const shouldShowPhoneLine = visibleCapturePhones.length > 0;
                const shouldShowWebsiteLine =
                  Boolean(captureWebsite) &&
                  normalizeCaptureDetailText(captureWebsite) !== normalizedTitleText;
                const customLinks = getCustomLinks(extracted);
                const customLinksTitle = getEditableCustomLinksTitle(extracted.customLinksTitle);
                const canMoveToPoster = !isPoster;
                const canMoveToAddress = !isPlace;
                const canMoveToEvent = extracted.captureType !== "event";
                const canMoveToContact = !isContact;
                const canSaveAddress = !isPlace && !isContact && Boolean(getCaptureAddress(extracted));
                const canShareInvite = !isPlace && !isContact && Boolean(extracted.posterImageDataUrl || extracted.title || extracted.date || customLinks.length);
                const isEditing = Number(editingId) === Number(item.id);
                const posterImageUrl = String(extracted.posterImageDataUrl || "").trim();
                const publicShareEnabled = isInvitePublic(extracted);
                const displayNotes = getDisplayNotes(extracted);
                const rawImageText = getRawImageText(extracted);
                const hasSecondaryActions = Boolean(
                  canMoveToPoster ||
                  canMoveToAddress ||
                  canMoveToEvent ||
                  canMoveToContact ||
                  canSaveAddress ||
                  canShareInvite ||
                  googleCalendarUrl
                );
                return (
                  <Box
                    key={item.id}
                    sx={{
                      bgcolor: "background.paper",
                      border: "1px solid #d8e1ea",
                      borderRadius: 1,
                      p: 2,
                      boxShadow: "0 8px 24px rgba(34,55,74,0.06)",
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.25}
                        alignItems={{ xs: "stretch", md: "flex-start" }}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          {!isEditing && (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography sx={{ fontWeight: 800, color: "text.primary", fontSize: 18 }}>
                                {getCaptureTitle(item)}
                              </Typography>
                              <Chip size="small" label={displayCaptureStatus(item)} color={statusColor(displayCaptureStatus(item))} />
                              {canShareInvite && (
                                <Chip
                                  size="small"
                                  label={publicShareEnabled ? "Public sharing on" : "Private"}
                                  variant="outlined"
                                  sx={{
                                    borderColor: publicShareEnabled ? "#0f766e" : "#c8d3df",
                                    color: publicShareEnabled ? "#0f766e" : "#5f6f7d",
                                    fontWeight: 700,
                                  }}
                                />
                              )}
                            </Stack>
                          )}
                        </Box>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          flexWrap="wrap"
                          sx={{
                            flexShrink: 0,
                            maxWidth: { xs: "100%", md: "48%" },
                            "& .MuiButton-root": {
                              minHeight: 36,
                              px: 1.35,
                              fontSize: 13,
                              borderRadius: 1,
                              whiteSpace: "nowrap",
                            },
                          }}
                        >
                          {isEditing ? (
                            <>
                              <Button
                                variant="contained"
                                startIcon={savingId === item.id ? <CircularProgress color="inherit" size={18} /> : <SaveIcon />}
                                disabled={savingId === item.id || (isEditing && !editDateValid)}
                                onClick={() => saveDraft(item.id)}
                                sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                              >
                                Save
                              </Button>
                              <Tooltip title="Cancel">
                                <IconButton aria-label="Cancel editing" onClick={cancelEdit} sx={actionIconSx}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              {!isPlace && !isContact && !isPoster && (
                                <>
                                  {(canAdd || hasCalendarItem) && (
                                    <FormControl size="small" sx={{ minWidth: { xs: 138, sm: 150 } }}>
                                      <InputLabel id={`capture-reminder-actions-${item.id}`}>Reminder</InputLabel>
                                      <Select
                                        labelId={`capture-reminder-actions-${item.id}`}
                                        label="Reminder"
                                        value={reminderValues[item.id] ?? 1440}
                                        onChange={(event) =>
                                          setReminderValues((current) => ({
                                            ...current,
                                            [item.id]: Number(event.target.value),
                                          }))
                                        }
                                      >
                                        {REMINDER_OPTIONS.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  )}
                                  {hasCalendarItem ? (
                                    <Button
                                      variant="outlined"
                                      startIcon={reminderSavingId === item.id ? <CircularProgress size={18} /> : <SaveIcon />}
                                      disabled={reminderSavingId === item.id}
                                      onClick={() => updateCalendarReminder(item.id)}
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                    >
                                      Save reminder
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      startIcon={savingId === item.id ? <CircularProgress color="inherit" size={18} /> : <AddTaskIcon />}
                                      disabled={!canAdd || savingId === item.id}
                                      onClick={() => addToCalendar(item.id)}
                                      sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
                                    >
                                      Add to Timely365
                                    </Button>
                                  )}
                                </>
                              )}
                              {canShareInvite && (
                                <Tooltip title="Copy share link">
                                  <span>
                                    <IconButton
                                      aria-label="Copy share link"
                                      onClick={() => copyInviteLink(item)}
                                      disabled={sharingId === item.id}
                                      sx={actionIconSx}
                                    >
                                      {sharingId === item.id ? <CircularProgress size={18} /> : <ContentCopyIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              {canShareInvite && (
                                <Button
                                  variant={publicShareEnabled ? "outlined" : "contained"}
                                  size="small"
                                  startIcon={
                                    sharingId === item.id
                                      ? <CircularProgress color={publicShareEnabled ? "primary" : "inherit"} size={16} />
                                      : publicShareEnabled
                                        ? <LockOutlinedIcon fontSize="small" />
                                        : <LinkIcon fontSize="small" />
                                  }
                                  onClick={() =>
                                    publicShareEnabled
                                      ? disableInviteLink(item.id)
                                      : createInviteLink(item.id, { copy: false, open: false, publicShare: true })
                                  }
                                  disabled={sharingId === item.id}
                                  sx={
                                    publicShareEnabled
                                      ? { borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }
                                      : { bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }
                                  }
                                >
                                  {publicShareEnabled ? "Make private" : "Make public"}
                                </Button>
                              )}
                              {compactActions ? (
                                <>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    endIcon={<MoreVertIcon />}
                                    onClick={(event) => {
                                      setActionMenuAnchor(event.currentTarget);
                                      setActionMenuItemId(item.id);
                                    }}
                                    sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                  >
                                    Actions
                                  </Button>
                                  <Menu
                                    anchorEl={actionMenuAnchor}
                                    open={Number(actionMenuItemId) === Number(item.id)}
                                    onClose={closeActionMenu}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                  >
                                    <MenuItem
                                      onClick={() => {
                                        closeActionMenu();
                                        startEdit(item);
                                      }}
                                    >
                                      <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                      <ListItemText>Edit capture</ListItemText>
                                    </MenuItem>
                                    {canMoveToPoster && (
                                      <MenuItem
                                        disabled={savingId === item.id || (isEditing && !editDateValid)}
                                        onClick={() => {
                                          closeActionMenu();
                                          moveCaptureToType(item, "poster");
                                        }}
                                      >
                                        <ListItemIcon><ImageSearchIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Move to Ad Poster</ListItemText>
                                      </MenuItem>
                                    )}
                                    {canMoveToAddress && (
                                      <MenuItem
                                        disabled={savingId === item.id || (isEditing && !editDateValid)}
                                        onClick={() => {
                                          closeActionMenu();
                                          moveCaptureToType(item, "place");
                                        }}
                                      >
                                        <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Move to Address</ListItemText>
                                      </MenuItem>
                                    )}
                                    {canMoveToEvent && (
                                      <MenuItem
                                        disabled={savingId === item.id || (isEditing && !editDateValid)}
                                        onClick={() => {
                                          closeActionMenu();
                                          moveCaptureToType(item, "event");
                                        }}
                                      >
                                        <ListItemIcon><EventAvailableIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Move to Event</ListItemText>
                                      </MenuItem>
                                    )}
                                    {canMoveToContact && (
                                      <MenuItem
                                        disabled={savingId === item.id || (isEditing && !editDateValid)}
                                        onClick={() => {
                                          closeActionMenu();
                                          moveCaptureToType(item, "contact");
                                        }}
                                      >
                                        <ListItemIcon><PersonAddAltIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Move to Contact</ListItemText>
                                      </MenuItem>
                                    )}
                                    {canSaveAddress && (
                                      <MenuItem
                                        disabled={savingPlaceId === item.id}
                                        onClick={() => {
                                          closeActionMenu();
                                          saveAddress(item.id);
                                        }}
                                      >
                                        <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save address</ListItemText>
                                      </MenuItem>
                                    )}
                                    {googleCalendarUrl && (
                                      <MenuItem
                                        component="a"
                                        href={googleCalendarUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={closeActionMenu}
                                      >
                                        <ListItemIcon><GoogleIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText>Add to Google Calendar</ListItemText>
                                      </MenuItem>
                                    )}
                                    <MenuItem
                                      disabled={removingId === item.id}
                                      onClick={() => {
                                        closeActionMenu();
                                        removeDraft(item);
                                      }}
                                      sx={{ color: "error.main" }}
                                    >
                                      <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
                                      <ListItemText>Remove capture</ListItemText>
                                    </MenuItem>
                                  </Menu>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Edit capture">
                                    <IconButton aria-label="Edit capture" onClick={() => startEdit(item)} sx={actionIconSx}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {hasSecondaryActions && (
                                    <>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<MoreVertIcon />}
                                        onClick={(event) => {
                                          setActionMenuAnchor(event.currentTarget);
                                          setActionMenuItemId(item.id);
                                        }}
                                        sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                      >
                                        More
                                      </Button>
                                      <Menu
                                        anchorEl={actionMenuAnchor}
                                        open={Number(actionMenuItemId) === Number(item.id)}
                                        onClose={closeActionMenu}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                      >
                                        {canMoveToPoster && (
                                          <MenuItem
                                            disabled={savingId === item.id || (isEditing && !editDateValid)}
                                            onClick={() => {
                                              closeActionMenu();
                                              moveCaptureToType(item, "poster");
                                            }}
                                          >
                                            <ListItemIcon><ImageSearchIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Move to Ad Poster</ListItemText>
                                          </MenuItem>
                                        )}
                                        {canMoveToAddress && (
                                          <MenuItem
                                            disabled={savingId === item.id || (isEditing && !editDateValid)}
                                            onClick={() => {
                                              closeActionMenu();
                                              moveCaptureToType(item, "place");
                                            }}
                                          >
                                            <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Move to Address</ListItemText>
                                          </MenuItem>
                                        )}
                                        {canMoveToEvent && (
                                          <MenuItem
                                            disabled={savingId === item.id || (isEditing && !editDateValid)}
                                            onClick={() => {
                                              closeActionMenu();
                                              moveCaptureToType(item, "event");
                                            }}
                                          >
                                            <ListItemIcon><EventAvailableIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Move to Event</ListItemText>
                                          </MenuItem>
                                        )}
                                        {canMoveToContact && (
                                          <MenuItem
                                            disabled={savingId === item.id || (isEditing && !editDateValid)}
                                            onClick={() => {
                                              closeActionMenu();
                                              moveCaptureToType(item, "contact");
                                            }}
                                          >
                                            <ListItemIcon><PersonAddAltIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Move to Contact</ListItemText>
                                          </MenuItem>
                                        )}
                                        {canSaveAddress && (
                                          <MenuItem
                                            disabled={savingPlaceId === item.id}
                                            onClick={() => {
                                              closeActionMenu();
                                              saveAddress(item.id);
                                            }}
                                          >
                                            <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Save address</ListItemText>
                                          </MenuItem>
                                        )}
                                        {googleCalendarUrl && (
                                          <MenuItem
                                            component="a"
                                            href={googleCalendarUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={closeActionMenu}
                                          >
                                            <ListItemIcon><GoogleIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Add to Google Calendar</ListItemText>
                                          </MenuItem>
                                        )}
                                      </Menu>
                                    </>
                                  )}
                                  <Tooltip title="Remove capture">
                                    <span>
                                      <IconButton
                                        aria-label="Remove capture"
                                        disabled={removingId === item.id}
                                        onClick={() => removeDraft(item)}
                                        sx={dangerIconSx}
                                      >
                                        {removingId === item.id ? <CircularProgress size={18} /> : <DeleteOutlineIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                            </>
                          )}
                        </Stack>
                      </Stack>
                      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                        {isEditing ? (
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 640px)",
                              gap: 1.5,
                              alignItems: "flex-start",
                              maxWidth: 640,
                            }}
                          >
                            <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                              {isPoster && (
                                <TextField
                                  label="Capture name"
                                  size="small"
                                  value={editValues.internalName || ""}
                                  placeholder="Graduation poster, Spring promo, Telegram ad"
                                  helperText="Only shown in Capture so you can tell posters apart."
                                  onChange={(event) => setEditValues((current) => ({ ...current, internalName: event.target.value }))}
                                />
                              )}
                              <TextField
                                label={isPoster ? "Public poster title" : "Title"}
                                size="small"
                                value={editValues.title || ""}
                                helperText={isPoster ? "Shown on the shared browser page." : undefined}
                                onChange={(event) => setEditValues((current) => ({ ...current, title: event.target.value }))}
                              />
                            {isContact && (
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                <TextField
                                  label="Business name"
                                  size="small"
                                  value={editValues.businessName || ""}
                                  onChange={(event) => setEditValues((current) => ({ ...current, businessName: event.target.value }))}
                                />
                                <TextField
                                  label="Contact name"
                                  size="small"
                                  value={editValues.contactName || ""}
                                  onChange={(event) => setEditValues((current) => ({ ...current, contactName: event.target.value }))}
                                />
                              </Stack>
                            )}
                            {!isPlace && !isContact && !isPoster && (
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                <CalendarDateField label="Date" value={editValues.date || ""} calendar={displayCalendar} onChange={date => setEditValues(current => ({ ...current, date }))} onValidityChange={setEditDateValid} />
                                <CalendarTimeField label="Time" value={clockValue(editValues)} clock={displayClock} onChange={value => setEditValues(current => ({ ...current, ...getEditTimeParts(value) }))} />
                              </Stack>
                            )}
                            {!isPoster && (
                              <TextField
                                label={isPlace || isContact ? "Address" : "Location"}
                                size="small"
                                value={isPlace || isContact ? editValues.address || "" : editValues.location || ""}
                                onChange={(event) => setEditValues((current) => ({
                                  ...current,
                                  ...(isPlace || isContact ? { address: event.target.value, location: event.target.value } : { location: event.target.value }),
                                }))}
                              />
                            )}
                            {!isPlace && !isContact && !isPoster && (
                              <Box sx={{ border: "1px solid #d8e1ea", borderRadius: 1, p: 1.25, bgcolor: "background.default" }}>
                                <Typography sx={{ fontWeight: 800, color: "text.primary", fontSize: 14 }}>
                                  Public link visibility
                                </Typography>
                                <Typography sx={{ color: "text.secondary", fontSize: 12, mb: 0.75 }}>
                                  Hide scanned details that are not real or should not appear on the public poster link.
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={normalizePublicVisibility(editValues.publicVisibility).showDateTime}
                                        onChange={(event) =>
                                          setEditValues((current) => ({
                                            ...current,
                                            publicVisibility: {
                                              ...normalizePublicVisibility(current.publicVisibility),
                                              showDateTime: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                    }
                                    label="Show date and time"
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={normalizePublicVisibility(editValues.publicVisibility).showLocation}
                                        onChange={(event) =>
                                          setEditValues((current) => ({
                                            ...current,
                                            publicVisibility: {
                                              ...normalizePublicVisibility(current.publicVisibility),
                                              showLocation: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                    }
                                    label="Show location or address"
                                  />
                                </Stack>
                              </Box>
                            )}
                            {(isPlace || isContact) && (
                              <Stack spacing={1}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                  <Typography sx={{ color: "text.primary", fontSize: 14, fontWeight: 800 }}>
                                    Phones
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={addEditPhone}
                                    sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                  >
                                    Add phone
                                  </Button>
                                </Box>
                                {(Array.isArray(editValues.phones) && editValues.phones.length ? editValues.phones : [""]).map((phone, index) => (
                                  <Stack key={`capture-phone-${index}`} direction="row" spacing={1} alignItems="center">
                                    <TextField
                                      label={index === 0 ? "Primary phone" : `Phone ${index + 1}`}
                                      size="small"
                                      value={phone || ""}
                                      onChange={(event) => handleEditPhoneChange(index, event.target.value)}
                                      sx={{ flex: 1 }}
                                    />
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      disabled={(editValues.phones || [""]).length === 1}
                                      onClick={() => removeEditPhone(index)}
                                    >
                                      Remove
                                    </Button>
                                  </Stack>
                                ))}
                                <TextField
                                  label="Website"
                                  size="small"
                                  value={editValues.website || ""}
                                  onChange={(event) => setEditValues((current) => ({ ...current, website: event.target.value }))}
                                />
                              </Stack>
                            )}
                            <TextField
                              label="Notes"
                              size="small"
                              value={editValues.notes || ""}
                              onChange={(event) => setEditValues((current) => ({ ...current, notes: event.target.value }))}
                              multiline
                              minRows={2}
                            />
                              <Box sx={{ border: "1px solid #d8e1ea", borderRadius: 1, p: 1.25, bgcolor: "background.default" }}>
                                <Stack spacing={1}>
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, color: "text.primary", fontSize: 14 }}>
                                      Custom poster links
                                    </Typography>
                                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                                      Add buttons for Telegram, registration, your website, forms, or any call to action.
                                    </Typography>
                                  </Box>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
                                    <TextField
                                      label="Title shown above links"
                                      size="small"
                                      value={editValues.customLinksTitle || ""}
                                      placeholder="Registration links"
                                      helperText="Optional. Leave blank to show only the buttons on the shared browser page."
                                      onChange={(event) =>
                                        setEditValues((current) => ({
                                          ...current,
                                          customLinksTitle: event.target.value,
                                        }))
                                      }
                                      fullWidth
                                    />
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled={!String(editValues.customLinksTitle || "").trim()}
                                      onClick={() =>
                                        setEditValues((current) => ({
                                          ...current,
                                          customLinksTitle: "",
                                        }))
                                      }
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper", whiteSpace: "nowrap" }}
                                    >
                                      Remove title
                                    </Button>
                                  </Stack>
                                  {(editValues.customLinks || []).map((link, index) => (
                                    <Stack key={`custom-link-${index}`} direction={{ xs: "column", sm: "row" }} spacing={1}>
                                      <TextField
                                        label="Label"
                                        size="small"
                                        value={link.label || ""}
                                        placeholder="Telegram"
                                        onChange={(event) =>
                                          setEditValues((current) => {
                                            const customLinks = [...(current.customLinks || [])];
                                            customLinks[index] = { ...customLinks[index], label: event.target.value };
                                            return { ...current, customLinks };
                                          })
                                        }
                                        sx={{ flex: 1 }}
                                      />
                                      <TextField
                                        label="Link"
                                        size="small"
                                        value={link.url || ""}
                                        placeholder="https://t.me/yourchannel"
                                        onChange={(event) =>
                                          setEditValues((current) => {
                                            const customLinks = [...(current.customLinks || [])];
                                            customLinks[index] = { ...customLinks[index], url: event.target.value };
                                            return { ...current, customLinks };
                                          })
                                        }
                                        sx={{ flex: 2 }}
                                      />
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() =>
                                          setEditValues((current) => ({
                                            ...current,
                                            customLinks: (current.customLinks || []).filter((_, linkIndex) => linkIndex !== index),
                                          }))
                                        }
                                      >
                                        Remove
                                      </Button>
                                    </Stack>
                                  ))}
                                  <Button
                                    variant="outlined"
                                    startIcon={<LinkIcon />}
                                    onClick={() =>
                                      setEditValues((current) => ({
                                        ...current,
                                        customLinks: [...(current.customLinks || []), { label: "", url: "" }],
                                      }))
                                    }
                                    sx={{ alignSelf: "flex-start", borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                  >
                                    Add custom link
                                  </Button>
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            {isSavedInfo && (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: posterImageUrl ? "112px minmax(0, 1fr)" : "minmax(0, 1fr)",
                                    md: posterImageUrl ? "132px minmax(0, 1fr) auto" : "minmax(0, 1fr) auto",
                                  },
                                  gap: 1.25,
                                  alignItems: "center",
                                  maxWidth: "100%",
                                  mb: 0.75,
                                }}
                              >
                                {posterImageUrl && (
                                  <Box
                                    sx={{
                                      width: { xs: "100%", sm: 112, md: 132 },
                                      maxWidth: "100%",
                                      border: "1px solid #d8e1ea",
                                      borderRadius: 1,
                                      overflow: "hidden",
                                      bgcolor: "background.default",
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={posterImageUrl}
                                      alt={extracted.title || "Captured image"}
                                      sx={{
                                        display: "block",
                                        width: "100%",
                                        height: { xs: 160, sm: 96, md: 108 },
                                        objectFit: "contain",
                                      }}
                                    />
                                  </Box>
                                )}
                                <Stack spacing={0.45} sx={{ minWidth: 0 }}>
                                  {shouldShowAddressLine && (
                                    <Typography sx={{ color: "#394b59", fontWeight: 700, overflowWrap: "anywhere" }}>
                                      Address: {captureAddress}
                                    </Typography>
                                  )}
                                  {shouldShowPhoneLine && (
                                    <Typography sx={{ color: "text.secondary", fontSize: 14, overflowWrap: "anywhere" }}>
                                      Phone{visibleCapturePhones.length === 1 ? "" : "s"}: {visibleCapturePhones.join(", ")}
                                    </Typography>
                                  )}
                                  {shouldShowWebsiteLine && (
                                    <Typography sx={{ color: "text.secondary", fontSize: 14, overflowWrap: "anywhere" }}>
                                      Website: {captureWebsite}
                                    </Typography>
                                  )}
                                  {!captureAddress && !capturePhone && !captureWebsite && (
                                    <Typography sx={{ color: "#7a8793", fontSize: 14 }}>
                                      No address, phone, or website was extracted yet.
                                    </Typography>
                                  )}
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                  flexWrap="wrap"
                                  sx={{
                                    gridColumn: { xs: "1", sm: posterImageUrl ? "2" : "1", md: "auto" },
                                    "& .MuiButton-root": {
                                      minHeight: 34,
                                      px: 1.2,
                                      fontSize: 13,
                                      borderRadius: 1,
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                >
                                  {directionsUrl && (
                                    <Button
                                      component="a"
                                      href={directionsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                      startIcon={<PlaceIcon />}
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                    >
                                      Directions
                                    </Button>
                                  )}
                                  {capturePhone && (
                                    <Button
                                      component="a"
                                      href={`tel:${capturePhone}`}
                                      variant="outlined"
                                      startIcon={<PhoneIcon />}
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                    >
                                      Call
                                    </Button>
                                  )}
                                  {captureWebsite && (
                                    <Button
                                      component="a"
                                      href={captureWebsite}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                      endIcon={<OpenInNewIcon />}
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                    >
                                      Website
                                    </Button>
                                  )}
                                  {(captureAddress || capturePhone || captureWebsite) && (
                                    <Tooltip title="Copy details">
                                      <IconButton
                                        aria-label="Copy contact details"
                                        onClick={async () => {
                                          const text = [
                                            getCaptureTitle(item),
                                            captureAddress ? `Address: ${captureAddress}` : "",
                                            capturePhones.length ? `Phone${capturePhones.length === 1 ? "" : "s"}: ${capturePhones.join(", ")}` : "",
                                            captureWebsite ? `Website: ${captureWebsite}` : "",
                                          ].filter(Boolean).join("\n");
                                          await navigator.clipboard?.writeText(text).catch(() => {});
                                          setNotice(isContact ? "Contact details copied." : "Address details copied.");
                                        }}
                                        sx={actionIconSx}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Box>
                            )}
                            {posterImageUrl && !isSavedInfo && (
                              <Box
                                component="button"
                                type="button"
                                onClick={() => openInviteLink(item)}
                                sx={{
                                  display: "block",
                                  position: "relative",
                                  width: { xs: "100%", sm: 220 },
                                  maxWidth: "100%",
                                  p: 0,
                                  border: "1px solid #d8e1ea",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  bgcolor: "background.default",
                                  mb: 0.75,
                                  cursor: "pointer",
                                  textAlign: "inherit",
                                  font: "inherit",
                                  "&:hover .poster-open-overlay, &:focus-visible .poster-open-overlay": {
                                    opacity: 1,
                                  },
                                  "&:focus-visible": {
                                    outline: "2px solid #22374a",
                                    outlineOffset: 2,
                                  },
                                }}
                              >
                                <Box
                                  component="img"
                                  src={posterImageUrl}
                                  alt={extracted.title || "Captured image"}
                                  sx={{
                                    display: "block",
                                    width: "100%",
                                    maxHeight: 220,
                                    objectFit: "contain",
                                  }}
                                />
                                <Box
                                  className="poster-open-overlay"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "rgba(34, 55, 74, 0.72)",
                                    color: "#fff",
                                    fontWeight: 900,
                                    fontSize: 14,
                                    opacity: 0,
                                    transition: "opacity 150ms ease",
                                  }}
                                >
                                  Click to open
                                </Box>
                              </Box>
                            )}
                            {!isPlace && !isContact && !isPoster && formatCaptureTime(extracted) && (
                              <Typography sx={{ color: "#394b59", fontWeight: 700 }}>
                                {formatCaptureTime(extracted)}
                              </Typography>
                            )}
                            {!isSavedInfo && getCaptureAddress(extracted) && (
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, maxWidth: { xs: "100%", md: 680 } }}>
                                <Typography
                                  sx={{
                                    color: "text.secondary",
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={getCaptureAddress(extracted)}
                                >
                                  {getCaptureAddress(extracted)}
                                </Typography>
                                {directionsUrl && (
                                  <Tooltip title="Google directions">
                                    <IconButton
                                      component="a"
                                      href={directionsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      sx={{ ...actionIconSx, width: 32, height: 32, flexShrink: 0 }}
                                    >
                                      <PlaceIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                            {!isSavedInfo && customLinks.length > 0 && (
                              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 700 }}>
                                    {customLinksTitle ? `Button title: ${customLinksTitle}` : "No title above buttons"}
                                  </Typography>
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<EditIcon fontSize="small" />}
                                    onClick={() => startEdit(item)}
                                    sx={{ minWidth: 0, p: 0.25, color: "#2563eb", fontWeight: 800 }}
                                  >
                                    Update link title
                                  </Button>
                                </Stack>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {customLinks.map((link, index) => (
                                    <Button
                                      key={`${link.url}-${index}`}
                                      component="a"
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      variant="outlined"
                                      size="small"
                                      startIcon={<LinkIcon />}
                                      endIcon={<OpenInNewIcon fontSize="small" />}
                                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                                    >
                                      {link.label}
                                    </Button>
                                  ))}
                                </Stack>
                              </Stack>
                            )}
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Container>
      <Dialog
        open={scanDialogOpen}
        onClose={() => {
          if (!scanSaving) setScanDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800, color: "text.primary" }}>
          Scan or Paste Capture
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5, minWidth: 0 }}>
            <Alert severity={captureScanLimitReached ? "warning" : "info"}>
              <Stack spacing={1} alignItems="flex-start">
                <span>
                  {captureScanLimit == null
                    ? `Your ${capturePlanTier.toLowerCase()} plan has unlimited Capture scans this month.`
                    : `${captureScanUsageLabel}. ${captureScansRemaining} scan${captureScansRemaining === 1 ? "" : "s"} remaining this month.`}
                </span>
                {captureScanLimitReached ? (
                  <Button
                    component={RouterLink}
                    to={BOOKING_PRICING_PATH}
                    variant="outlined"
                    size="small"
                    sx={{ borderColor: "#22374a", color: "text.primary" }}
                  >
                    View Booking plans
                  </Button>
                ) : null}
              </Stack>
            </Alert>
            <TextField
              label="Paste invite, address, contact, or image"
              value={scanText}
              onChange={(event) => setScanText(event.target.value)}
              onPaste={handleScanPaste}
              placeholder="Paste text here, or paste an image from your clipboard."
              multiline
              minRows={4}
              fullWidth
            />
            {scanImageDataUrl && (
              <Box
                sx={{
                  border: "1px solid #d8e1ea",
                  borderRadius: 1,
                  bgcolor: "background.default",
                  p: 1,
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={scanImageDataUrl}
                  alt={scanImageName || "Capture image"}
                  sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ borderColor: "#c8d3df", color: "text.primary" }}
              >
                Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    setScanImageFile(file);
                  }}
                />
              </Button>
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 }, flex: 1 }}>
                <InputLabel id="scan-reminder-label">Reminder</InputLabel>
                <Select
                  labelId="scan-reminder-label"
                  label="Reminder"
                  value={scanReminderMinutes}
                  onChange={(event) => setScanReminderMinutes(Number(event.target.value))}
                >
                  {REMINDER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              clearScanInput();
              setScanDialogOpen(false);
            }}
            disabled={scanSaving}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={scanSaving ? <CircularProgress color="inherit" size={18} /> : <ImageSearchIcon />}
            disabled={scanSaving || captureScanLimitReached || (!scanText.trim() && !scanImageDataUrl)}
            onClick={createScannedCapture}
            sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
          >
            Scan Capture
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(quickDialog)} onClose={closeQuickDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, color: "text.primary" }}>
          {quickDialog === "contact" ? "Add Contact" : quickDialog === "place" ? "Add Address" : "Quick Schedule"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {quickDialog === "place" || quickDialog === "contact" ? (
              <>
                <TextField
                  label="Label"
                  value={quickValues.title || ""}
                  onChange={(event) => updateQuickValue("title", event.target.value)}
                  placeholder={quickDialog === "contact" ? "John Smith, ACME dispatch" : "Dentist office, client address, venue"}
                  fullWidth
                />
                <TextField
                  label="Business name"
                  value={quickValues.businessName || ""}
                  onChange={(event) => updateQuickValue("businessName", event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Contact name"
                  value={quickValues.contactName || ""}
                  onChange={(event) => updateQuickValue("contactName", event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Address"
                  value={quickValues.address || ""}
                  onChange={(event) => updateQuickValue("address", event.target.value)}
                  fullWidth
                  required
                  multiline
                  minRows={2}
                />
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ color: "text.primary", fontSize: 14, fontWeight: 800 }}>
                      Phones
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={addQuickPhone}
                      sx={{ borderColor: "#c8d3df", color: "text.primary", bgcolor: "background.paper" }}
                    >
                      Add phone
                    </Button>
                  </Box>
                  {(Array.isArray(quickValues.phones) && quickValues.phones.length ? quickValues.phones : [""]).map((phone, index) => (
                    <Stack key={`quick-phone-${index}`} direction="row" spacing={1} alignItems="center">
                      <TextField
                        label={index === 0 ? "Primary phone" : `Phone ${index + 1}`}
                        value={phone || ""}
                        onChange={(event) => handleQuickPhoneChange(index, event.target.value)}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        disabled={(quickValues.phones || [""]).length === 1}
                        onClick={() => removeQuickPhone(index)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  ))}
                  <TextField
                    label="Website"
                    value={quickValues.website || ""}
                    onChange={(event) => updateQuickValue("website", event.target.value)}
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Notes"
                  value={quickValues.notes || ""}
                  onChange={(event) => updateQuickValue("notes", event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </>
            ) : (
              <>
                <TextField
                  label="Title"
                  value={quickValues.title || ""}
                  onChange={(event) => updateQuickValue("title", event.target.value)}
                  placeholder="Graduation party, dentist appointment"
                  fullWidth
                  required
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <CalendarDateField label="Date" value={quickValues.date || ""} calendar={displayCalendar} onChange={date => updateQuickValue("date", date)} onValidityChange={setQuickDateValid} />
                  <CalendarTimeField label="Time" value={clockValue(quickValues)} clock={displayClock} onChange={value => setQuickValues(current => ({ ...current, ...getEditTimeParts(value) }))} />
                </Stack>
                <TextField
                  label="Location"
                  value={quickValues.location || ""}
                  onChange={(event) => updateQuickValue("location", event.target.value)}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="quick-reminder-label">Reminder</InputLabel>
                  <Select
                    labelId="quick-reminder-label"
                    label="Reminder"
                    value={quickValues.reminderMinutes ?? 1440}
                    onChange={(event) => updateQuickValue("reminderMinutes", Number(event.target.value))}
                  >
                    {REMINDER_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Notes"
                  value={quickValues.notes || ""}
                  onChange={(event) => updateQuickValue("notes", event.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeQuickDialog} disabled={quickSaving} sx={{ color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createQuickCapture}
            disabled={quickSaving || !quickDateValid}
            startIcon={quickSaving ? <CircularProgress color="inherit" size={18} /> : quickDialog === "place" || quickDialog === "contact" ? <PersonAddAltIcon /> : <AddTaskIcon />}
            sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
          >
            {quickDialog === "contact" ? "Save Contact" : quickDialog === "place" ? "Save Address" : "Add Schedule"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
